export type AnalyticsEventParameters = {
  sign_up: { method: 'email' | 'google' | 'microsoft'; signup_source: string };
  onboarding_completed: { onboarding_version: string };
  template_selected: {
    template_source: 'user' | 'marketplace';
    space_type: string;
    template_category?: string;
  };
  board_created: {
    creation_source: 'dashboard' | 'template';
    from_template: boolean;
    workspace_context?: 'workspace' | 'personal';
  };
  first_card_added: { card_type: string; space_type: 'board'; creation_source: string };
  board_shared: { share_method: 'link' | 'embed' | 'email'; access_type: 'board' };
  participant_joined: { participant_type: 'guest'; join_source: 'board' };
  participant_contributed: {
    contribution_type: 'card' | 'comment' | 'vote' | 'chat';
    participant_type: 'guest' | 'member';
  };
  vote_started: { voting_style: string };
  session_completed: { completion_method: string };
  board_reopened: { open_source: 'direct'; days_since_created_bucket: string };
  second_board_created: { creation_source: 'dashboard' | 'template'; from_template: boolean };
};

export type AnalyticsEventName = keyof AnalyticsEventParameters;

type DotstormingAnalytics = {
  track: (eventName: string, parameters?: Record<string, unknown>) => void;
  identify?: (userId: string, properties?: Record<string, unknown>) => void;
};

type DotstormingAnalyticsDebug = { consent?: string };

declare global {
  interface Window {
    dotstormingAnalytics?: DotstormingAnalytics;
    dotstormingAnalyticsDebug?: DotstormingAnalyticsDebug;
  }
}

export const trackAnalytics = <T extends AnalyticsEventName>(
  eventName: T,
  parameters: AnalyticsEventParameters[T]
) => {
  if (typeof window === 'undefined' || !window.dotstormingAnalytics) return false;
  if (window.dotstormingAnalyticsDebug && window.dotstormingAnalyticsDebug.consent !== 'granted') {
    return false;
  }
  window.dotstormingAnalytics.track(eventName, parameters);
  return true;
};

const trackOnce = <T extends AnalyticsEventName>(
  storage: Storage | undefined,
  key: string,
  eventName: T,
  parameters: AnalyticsEventParameters[T]
) => {
  if (!storage) return;

  try {
    if (storage.getItem(key) === 'true') return;
  } catch {
    // Storage is best-effort; the event can still be sent.
  }

  if (!trackAnalytics(eventName, parameters)) return;

  try {
    storage.setItem(key, 'true');
  } catch {
    // A later duplicate is preferable to losing the event.
  }
};

export const trackAnalyticsOnce = <T extends AnalyticsEventName>(
  key: string,
  eventName: T,
  parameters: AnalyticsEventParameters[T]
) =>
  trackOnce(
    typeof window === 'undefined' ? undefined : window.localStorage,
    `dotstorming_analytics_event_${key}`,
    eventName,
    parameters
  );

export const trackAnalyticsOncePerSession = <T extends AnalyticsEventName>(
  key: string,
  eventName: T,
  parameters: AnalyticsEventParameters[T]
) =>
  trackOnce(
    typeof window === 'undefined' ? undefined : window.sessionStorage,
    `dotstorming_analytics_event_${key}`,
    eventName,
    parameters
  );

export const identifyAnalyticsUser = (userId: string, properties?: Record<string, unknown>) => {
  if (typeof window === 'undefined') return;
  window.dotstormingAnalytics?.identify?.(userId, properties);
};
